import { type KeyboardEvent, Suspense } from 'react'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui'
import { LazyPdfViewer } from '@/modules/projects/components/dialogs/LazyPdfViewer'
import type { DocumentPage } from '@/modules/projects/components/Workbench/types'
import { OpeningPage } from './OpeningPage'

const isTyping = (target: EventTarget) =>
  target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement

interface PageZoomDialogProps {
  page: DocumentPage
  // The pages of the statement the page is filed in, in order. A statement can cross files
  // and skip sheets, so the dialog walks this list rather than a range of one file.
  statementPages: DocumentPage[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onPageChange: (page: DocumentPage) => void
}

export const PageZoomDialog = ({
  page,
  statementPages,
  open,
  onOpenChange,
  onPageChange,
}: PageZoomDialogProps) => {
  const found = statementPages.findIndex((held) => held.number === page.number)
  const pages = found === -1 ? [page] : statementPages
  const index = Math.max(found, 0)

  const goTo = (position: number) => {
    const target = pages[Math.min(Math.max(position, 1), pages.length) - 1]
    if (target && target.number !== page.number) onPageChange(target)
  }

  const stepWithArrows = (event: KeyboardEvent) => {
    if (isTyping(event.target)) return
    const step =
      event.key === 'ArrowLeft' ? -1 : event.key === 'ArrowRight' ? 1 : 0
    if (step === 0) return
    event.preventDefault()
    goTo(index + 1 + step)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-[90vh] max-w-5xl flex-col gap-3 p-4"
        onKeyDown={stepWithArrows}
      >
        <DialogHeader className="pr-10">
          <DialogTitle className="truncate text-base">
            Page {index + 1} of {pages.length} · {page.source.fileName}
          </DialogTitle>
          <DialogDescription className="sr-only">
            The statement's pages at a size you can zoom.
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-hidden rounded-lg">
          <Suspense fallback={<OpeningPage />}>
            {/* No key: moving to another page, even in another file, keeps the zoom. */}
            <LazyPdfViewer
              url={page.source.fileUrl}
              initialPage={page.source.pageInFile}
              pageRange={{
                start: page.source.pageInFile,
                end: page.source.pageInFile,
              }}
              pageNavigation={{
                current: index + 1,
                total: pages.length,
                onGo: goTo,
              }}
            />
          </Suspense>
        </div>
      </DialogContent>
    </Dialog>
  )
}
